'use client';
import { AnimatePresence, motion } from 'framer-motion';
import { Search } from 'lucide-react';
import type { Session } from 'next-auth';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { SearchInput } from '@/components/search-input';
import { LogoIcon, LogoTextIcon } from '@/components/icons';
// import { socialLinks } from '@/data/social-links'
import { BookIcon } from '../icons/book';
import { BooksIcon } from '../icons/books';
import { UserMenu } from './user-menu';
import { isProdState } from '@/constants';
import { MY_LIBRARY_PATH } from '@/constants/my-library-route';
import type { UserData } from '@/hooks/use-profile';
import { NotificationsBell } from '@/features/notifications/components/notifications-bell';
import { createCatalogNavigateHandler } from '@/features/fanfic-card/consts/LISTING_FANDOM_NAVIGATE_EVENT';
// import NotificationIcon from '../icons/notifications'

interface DesktopHeaderProps {
  isAuthenticated: boolean;
  userData: UserData | null;
  user: Session['user'] | undefined;
  onOpenAuthDialog: () => void;
  onLogout: () => void;
  // theme: string;
  // isDark: boolean;
  // toggleTheme: () => void;
  // mounted: boolean;
  isSearchActive: boolean;
  onSearchStateChange: (isActive: boolean) => void;
}

export function DesktopHeader({
  isAuthenticated,
  userData,
  user,
  onOpenAuthDialog,
  onLogout,
  // theme,
  // isDark,
  // toggleTheme,
  // mounted,
  isSearchActive,
  onSearchStateChange,
}: DesktopHeaderProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputContainerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  const getLinkClassName = (href: string) => {
    const baseClass = 'transition-colors';
    const isActive = isActiveLink(href);

    if (isActive) {
      return `${baseClass} text-white text-sm border-b-2 border-[#FF3DAE] pt-[1px] h-10 flex items-center justify-center`;
    }

    return `${baseClass} text-[#898989] text-sm hover:text-white h-10 flex items-center justify-center`;
  };

  useEffect(() => {
    if (!isSearchOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (searchInputContainerRef.current?.contains(target)) return;

      setIsSearchOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isSearchOpen]);

  const authorCabinetButtonClass =
    'h-10 text-[14px] bg-[#25282E] md:tracking-[-0.0058em] font-medium hover:bg-[#4a4f57] rounded-lg items-center justify-center transition-colors py-2.5 px-2 text-white gap-2 flex-shrink-0 flex';

  return (
    <div className="hidden lg:flex items-center justify-between w-full">
      <div className="flex items-center space-x-8">
        <div className="flex items-center justify-center space-x-0">
          <Link href="/" className="flex items-center gap-1">
            <LogoIcon className="w-8 h-8.5 bg-[#25282e] rounded-md flex items-center justify-center" />
            <LogoTextIcon className="text-2xl font-bold text-white pb-2 w-fit" />
          </Link>
        </div>

        <nav className="flex items-center space-x-5">
          <Link
            href="/fanfics"
            className={getLinkClassName('/fanfics')}
            onNavigate={createCatalogNavigateHandler('/fanfics')}
          >
            Фанфики
          </Link>
          <Link
            href="/originals"
            className={getLinkClassName('/originals')}
            onNavigate={createCatalogNavigateHandler('/originals')}
          >
            Ориджиналы
          </Link>
          <Link href="/media" className={getLinkClassName('/media')}>
            Медиа
          </Link>
          <Link href="/wiki" className={getLinkClassName('/wiki')}>
            Вики
          </Link>
          <Link href="/newauthors" className={getLinkClassName('/newauthors')}>
            Авторам
          </Link>
          <Link href="/about" className={getLinkClassName('/about')}>
            О нас
          </Link>
        </nav>
      </div>

      <div className="flex items-center space-x-3 min-w-0 overflow-visible">
        <AnimatePresence>
          {!isSearchActive && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center space-x-3 flex-shrink-0"
            >
              {isAuthenticated && (
                <Link href="/author-dashboard" className={authorCabinetButtonClass}>
                  <BookIcon className="w-4 h-4 text-[#6E7384] shrink-0" />
                  Кабинет автора
                </Link>
              )}
              {isAuthenticated && !isProdState && (
                <Link href={MY_LIBRARY_PATH} className={authorCabinetButtonClass}>
                  <BooksIcon className="w-4 h-4 text-[#6E7384]" />
                  Моя библиотека
                </Link>
              )}
              {isAuthenticated && <NotificationsBell />}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="w-10 h-10 bg-[#25282E] hover:bg-[#4a4f57] rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                title="Поиск"
              >
                <Search className="w-3.5 h-3.5 text-[#575E74]" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ opacity: 0, maxWidth: 0 }}
              animate={{ opacity: 1, maxWidth: '300px' }}
              exit={{ opacity: 0, maxWidth: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden flex-shrink min-w-0 w-[180px] xl:w-[300px]"
            >
              <div ref={searchInputContainerRef} className="relative w-full min-w-0">
                <SearchInput
                  className="h-10"
                  placeholder="Название, жанр, теги..."
                  variant="desktop"
                  onSearchStateChange={onSearchStateChange}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* {mounted && (
          <button
            onClick={toggleTheme}
            className="w-10 h-10 bg-[#25282E] hover:bg-[#4a4f57] rounded-lg flex items-center justify-center transition-colors"
            title={isDark ? 'Светлая тема' : 'Темная тема'}
          >
            {isDark ? (
              <Sun className="w-4 h-4 text-[#898989]" />
            ) : (
              <Moon className="w-4 h-4 text-[#898989]" />
            )}
          </button>
        )} */}

        {/* <button
          className="w-10 h-10 bg-[#25282E] hover:bg-[#4a4f57] rounded-lg flex items-center justify-center transition-colors"
          title="Уведомления"
        >
          <NotificationIcon className="w-4 h-4 text-[#575E74]" />
        </button> */}
        {isAuthenticated ? (
          <UserMenu userData={userData} user={user} onLogout={onLogout} />
        ) : (
          <button
            className="bg-[#a8ea3a] hover:opacity-80 transition-opacity text-[#25282e] font-medium px-6 py-3 rounded-lg text-button-text"
            onClick={onOpenAuthDialog}
          >
            Войти
          </button>
        )}
      </div>
    </div>
  );
}
